import React from 'react';
import { Box } from '@mui/material';
import { useTheme } from '../../contexts/ThemeContext';

/**
 * Hexagonal Network Spinner
 * 육각형 네트워크 기반 로딩 스피너
 * 전략 시뮬레이션 게임 스타일
 * 다크/라이트 모드 자동 대응
 */
export const HexagonalNetworkSpinner = ({ size = 200 }) => {
  const { colors, isDark } = useTheme();
  
  // 테마별 색상
  const spinnerColor = isDark ? '#00BFFF' : '#500B71';
  const glowColor = isDark ? 'rgba(0, 191, 255, 0.8)' : 'rgba(80, 11, 113, 0.8)';
  const glowColorLight = isDark ? 'rgba(0, 191, 255, 0.2)' : 'rgba(80, 11, 113, 0.2)';

  // 육각형 좌표 계산
  const hexPoints = (cx, cy, r) => {
    const points = [];
    for (let i = 0; i < 6; i++) {
      const angle = (Math.PI / 3) * i;
      points.push([
        cx + r * Math.cos(angle),
        cy + r * Math.sin(angle)
      ]);
    }
    return points.map(p => p.join(',')).join(' ');
  };

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        width: size,
        height: size,
        position: 'relative'
      }}
    >
      <svg
        width={size}
        height={size}
        viewBox="0 0 200 200"
        xmlns="http://www.w3.org/2000/svg"
      >
        <defs>
          <filter id={`hexGlow-${isDark ? 'dark' : 'light'}`}>
            <feGaussianBlur stdDeviation="3" result="coloredBlur"/>
            <feMerge>
              <feMergeNode in="coloredBlur"/>
              <feMergeNode in="SourceGraphic"/>
            </feMerge>
          </filter>
          <radialGradient id={`hexGradient-${isDark ? 'dark' : 'light'}`}>
            <stop offset="0%" stopColor={spinnerColor} stopOpacity="0.8"/>
            <stop offset="100%" stopColor={spinnerColor} stopOpacity="0.2"/>
          </radialGradient>
        </defs>

        {/* 중앙 육각형들 - 다층 구조 */}
        {[70, 50, 30].map((radius, index) => (
          <g key={index}>
            <polygon
              points={hexPoints(100, 100, radius)}
              fill="none"
              stroke={spinnerColor}
              strokeWidth={3 - index * 0.5}
              opacity={0.3 + index * 0.2}
              filter={`url(#hexGlow-${isDark ? 'dark' : 'light'})`}
            >
              <animateTransform
                attributeName="transform"
                type="rotate"
                from={`${index * 30} 100 100`}
                to={`${360 + index * 30} 100 100`}
                dur={`${8 + index * 2}s`}
                repeatCount="indefinite"
              />
            </polygon>
            
            {/* 육각형 꼭짓점 노드 */}
            {Array.from({ length: 6 }).map((_, i) => {
              const angle = (Math.PI / 3) * i;
              const x = 100 + radius * Math.cos(angle);
              const y = 100 + radius * Math.sin(angle);
              return (
                <circle
                  key={i}
                  cx={x}
                  cy={y}
                  r={3 - index * 0.5}
                  fill={spinnerColor}
                  filter={`url(#hexGlow-${isDark ? 'dark' : 'light'})`}
                >
                  <animate
                    attributeName="opacity"
                    values="0.3;1;0.3"
                    dur={`${1.5 + i * 0.2}s`}
                    repeatCount="indefinite"
                  />
                </circle>
              );
            })}
          </g>
        ))}

        {/* 외부 연결 육각형들 */}
        {[0, 60, 120, 180, 240, 300].map((angle, i) => {
          const distance = 80;
          const cx = 100 + distance * Math.cos((angle * Math.PI) / 180);
          const cy = 100 + distance * Math.sin((angle * Math.PI) / 180);
          
          return (
            <g key={i}>
              <line
                x1="100"
                y1="100"
                x2={cx}
                y2={cy}
                stroke={spinnerColor}
                strokeWidth="1"
                opacity="0.3"
                filter={`url(#hexGlow-${isDark ? 'dark' : 'light'})`}
              >
                <animate
                  attributeName="opacity"
                  values="0.1;0.5;0.1"
                  dur={`${2 + i * 0.3}s`}
                  repeatCount="indefinite"
                />
              </line>
              
              <polygon
                points={hexPoints(cx, cy, 15)}
                fill="none"
                stroke={spinnerColor}
                strokeWidth="1.5"
                opacity="0.4"
              >
                <animate
                  attributeName="opacity"
                  values="0.2;0.8;0.2"
                  dur={`${1.5 + i * 0.2}s`}
                  begin={`${i * 0.2}s`}
                  repeatCount="indefinite"
                />
              </polygon>
              
              <circle
                cx={cx}
                cy={cy}
                r="4"
                fill={spinnerColor}
                filter={`url(#hexGlow-${isDark ? 'dark' : 'light'})`}
              >
                <animate
                  attributeName="r"
                  values="3;5;3"
                  dur={`${1.5 + i * 0.2}s`}
                  begin={`${i * 0.2}s`}
                  repeatCount="indefinite"
                />
              </circle>
            </g>
          );
        })}

        {/* 중앙 코어 */}
        <polygon
          points={hexPoints(100, 100, 12)}
          fill={`url(#hexGradient-${isDark ? 'dark' : 'light'})`}
          stroke={spinnerColor}
          strokeWidth="2"
          filter={`url(#hexGlow-${isDark ? 'dark' : 'light'})`}
        >
          <animate
            attributeName="opacity"
            values="0.6;1;0.6"
            dur="2s"
            repeatCount="indefinite"
          />
        </polygon>

        {/* 펄스 효과 */}
        <circle
          cx="100"
          cy="100"
          r="8"
          fill="none"
          stroke={spinnerColor}
          strokeWidth="2"
          opacity="0"
        >
          <animate
            attributeName="r"
            values="8;70"
            dur="3s"
            repeatCount="indefinite"
          />
          <animate
            attributeName="opacity"
            values="0.8;0"
            dur="3s"
            repeatCount="indefinite"
          />
        </circle>
      </svg>
    </Box>
  );
};

export default HexagonalNetworkSpinner;
